<template>
  <div class="card-base hms-root" id="hp-magazine">
    <h4 class="card-title mb-0 text-ellipsis">{{ $t("homePage.cardMagazine") }}</h4>
    <p class="text-sm m-0 mt-2 line-clamp-2">{{ $t("homePage.subMagazine") }}</p>

    <div v-if="articles.length === 0" class="text-center state-center">
      <p >{{ $t("homePage.noMagazine") }}</p>
    </div>

    <div v-else class="hms-container">
      <div
        v-for="(article, index) in articles"
        :key="index"
        class="hms-article overflow-hidden"
      >
        <div class="hms-category">
          <div class="hms-category-label">{{ article.category }}</div>
          <div class="hms-category-tail"></div>
        </div>
        <div class="hms-image">
          <img :src="article.pic" alt="CDLAN SpA Magazine" width="295" height="160" />
        </div>
        <div class="hms-text">
          <div class="flex items-center gap-2 mt-2 mb-2">
            <img src="@/assets/img/calendar.png" alt="" width="21" height="21" />
            <span class="text-sm text-navy">{{ formatDate(article.date) }}</span>
          </div>
          <h3 class="hms-title">{{ article.title }}</h3>
          <p
            class="text-sm mt-3"
            v-html="
              article.abstract.length > 200
                ? article.abstract.slice(0, 200) + '&hellip;'
                : article.abstract
            "
          ></p>
        </div>
        <div class="hms-footer flex justify-between items-end">
          <div class="flex items-center gap-2 mt-2 mb-2">
            <img src="@/assets/img/user.png" alt="" width="21" height="21" />
            <span class="text-sm text-gray">{{ article.author }}</span>
          </div>
          <div class="hms-read-all">
            <a
              :href="article.link"
              target="_blank"
              class="text-white text-sm text-ellipsis"
              :title="$t('homePage.readAll')"
            >
              {{ $t("homePage.readAll") }}
            </a>
          </div>
        </div>
      </div>

      <div class="hms-goto">
        <ActionButton
          :title="$t('homePage.gotoMagazine')"
          custom-class="cdlButton"
          @click="openMagazine"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import ActionButton from "@/components/Primitives/Buttons/ActionButton.vue";

/**
 * @component HomeMagazineSection
 * @description Sezione Magazine della Home Page. Mostra gli articoli RSS di CDLAN Magazine
 * con immagine, categoria, titolo, autore e abstract troncato a 200 caratteri.
 * In assenza di articoli visualizza uno stato vuoto. Con articoli, espone un pulsante
 * "Vai al Magazine" che apre magazine.cdlan.it in una nuova scheda.
 *
 * @prop {Array} articles - Articoli RSS dal composable `useHomeMagazines`.
 *   Ogni elemento deve contenere: `{ title, abstract, date, author, pic, link, category }`.
 *
 * @slot — nessuno slot esposto
 */
defineProps({
  articles: { type: Array, default: () => [] },
});

function formatDate(date) {
  return moment(date).format("DD/MM/YYYY");
}

function openMagazine() {
  window.open("https://magazine.cdlan.it/", "_blank");
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.hms-root {
  min-height: 100px;
  padding: 10px 20px;
}

.hms-sub {
  margin-top: -10px;
}

.hms-container {
  position: relative;
  padding: 0 10px;
}

.hms-article {
  background-color: $greenFive;
  padding: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  margin: 20px 0;
}

.hms-category {
  position: absolute;
  left: 260px;
  min-width: 110px;
  max-width: 150px;
  float: right;
  margin-top: 20px;
}

.hms-category-label {
  background-color: $bluSix;
  color: white;
  padding: 10px;
  position: relative;
  left: 0;
  font-family: OpenSansBold;
}

.hms-category-tail {
  min-width: 20px;
  max-width: 20px;
  min-height: 20px;
  max-height: 20px;
  background-color: black;
  position: relative;
  left: 90%;
  clip-path: polygon(calc(100% - 30px) 0%, 0% 50%, 0% 100%, 50% 100%, 100% 30px);
  transform: rotate(90deg);
}

.hms-image img {
  border-radius: 15px;
  width: 100%;
  max-height: 160px;
}

.hms-text {
  padding: 5px 15px;
}

.hms-title {
  margin: 12px auto;
  font-size: $fontMiddle;
  color: $bluFour;
  line-height: $textLineHeight;
  line-height: 1.4;
}

.hms-footer {
  padding: 0 15px;
}

.hms-read-all {
  background-color: $bluSix;
  padding: 20px;
  position: relative;
  right: -23px;
  bottom: -10px;
  border-top-left-radius: 20px;
}

// larghezza fissa: la linguetta resta uguale in ogni lingua
.hms-read-all a {
  display: block;
  width: $readAllWidth;
  text-align: center;
  text-decoration: none;
}

.hms-goto {
  margin: 50px 0;
}

// "Vai al Magazine": larghezza fissa come i pulsanti delle card DC / Contattaci
.hms-goto :deep(.cdlButton) {
  display: block;
  width: $ctaButtonWidth;
  max-width: 100%;
  margin: 0 auto;
}

.hms-goto :deep(.ab-text) {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>